import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { unwrap } from '@/api/envelope';
import { createOrganization } from '@/api/generated';
import { ROUTES } from '@/app/routes';
import { PageHeader } from '@/components/ui/PageHeader';

import { OrganizationForm, type OrganizationFormHandle } from './OrganizationForm';
import {
  EMPTY_ORGANIZATION,
  toCreatePayload,
  type OrganizationFormOutput,
} from './organizationSchema';

export function CreateOrganizationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState(false);
  const form = useRef<OrganizationFormHandle>(null);
  const alertRef = useRef<HTMLDivElement>(null);

  /*
   * One render after the form was reset, not in the same tick as the save. The
   * leave prompt reads the form's dirty flag, and navigating before the reset
   * has reached it would ask "Leave without saving?" about a save that just
   * succeeded.
   */
  useEffect(() => {
    if (created) void navigate(ROUTES.staff.organizations);
  }, [created, navigate]);

  /*
   * Bring a failure into view.
   *
   * The form is long. Somebody fills it in, scrolls to the bottom, presses
   * Create - and the message appears at the top, out of sight. Measured at
   * 1440x900 it sat 390 pixels above the window, so the page looked as though
   * the button had done nothing at all.
   *
   * Focus goes with the scroll, so a screen reader announces it too rather than
   * only sighted users noticing the page has moved.
   */
  useEffect(() => {
    if (!formError) return;
    alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    alertRef.current?.focus();
  }, [formError]);

  const create = useMutation({
    mutationFn: async (values: OrganizationFormOutput) => {
      const reply = await createOrganization({
        body: toCreatePayload(values),
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => {
      setFormError(null);
    },
    onSuccess: async () => {
      // Drop the cached list so the new account is already there when we arrive.
      await queryClient.invalidateQueries({ queryKey: ['staff', 'organizations'] });
      // Saved means clean: nothing typed here is unsaved any more.
      form.current?.reset(EMPTY_ORGANIZATION);
      setCreated(true);
    },
    onError: (error) => {
      // Each field's message lands on its field - a name clash on Name, which
      // takes focus and with it comes into view. Whatever concerns no field
      // comes back to sit in the alert above the form.
      const { remainder } = form.current?.applyServerErrors(error) ?? {
        remainder: 'The organization could not be created. Please try again.',
      };
      if (remainder) setFormError(remainder);
    },
  });

  return (
    <Box>
      <PageHeader
        title="New organization"
        description="Create an account for an organization Activist Technologies has approved."
      />

      {formError ? (
        <Alert
          ref={alertRef}
          severity="error"
          role="alert"
          tabIndex={-1}
          sx={{ mb: 3, maxWidth: 760, '&:focus': { outline: 'none' } }}
        >
          {formError}
        </Alert>
      ) : null}

      <OrganizationForm
        ref={form}
        onSubmit={(values) => create.mutate(values)}
        pending={create.isPending}
        submitLabel="Create organization"
      />
    </Box>
  );
}
